import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiClient } from "@/lib/api";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 8;

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const nav = useNavigate();
  const [status, setStatus] = useState("polling"); // polling | paid | unpaid | expired | error
  const [info, setInfo] = useState(null);
  const attempts = useRef(0);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await apiClient.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setInfo(data);
        if (data.payment_status === "paid") {
          setStatus("paid");
          return;
        }
        if (data.session_status === "expired") {
          setStatus("expired");
          return;
        }
        attempts.current += 1;
        if (attempts.current >= MAX_ATTEMPTS) {
          setStatus("unpaid");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
    // sessionId is the only runtime input; MAX_ATTEMPTS/POLL_INTERVAL are module constants
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const isPaid = status === "paid";
  const isPolling = status === "polling";

  let title = "Vérification du règlement…";
  let body = "Nous validons votre opération auprès de notre prestataire de paiement.";
  let Icon = Loader2;
  let iconCls = "text-[#C9A961] animate-spin";

  if (isPaid) {
    title = "Règlement validé";
    body = info?.kind === "subscription"
      ? `Votre référencement « ${info.tier} » est désormais actif. Une confirmation vous a été adressée.`
      : info?.kind === "lawyer_dossier_unlock"
      ? "Le dossier de l'avocat a été débloqué. CONFILEX a déclenché la création de la fiche complète et envoyé une invitation au professionnel."
      : "Votre paiement a bien été reçu. Le spécialiste va prendre contact avec vous.";
    Icon = CheckCircle2;
    iconCls = "text-[#7A9B7E]";
  } else if (status === "expired" || status === "unpaid") {
    title = "Règlement non finalisé";
    body = "Votre paiement n'a pas pu être confirmé. Vous pouvez réessayer ou nous contacter si la difficulté persiste.";
    Icon = XCircle;
    iconCls = "text-[#8B3A3A]";
  } else if (status === "error") {
    title = "Vérification impossible";
    body = "Nous n'avons pas pu vérifier votre règlement. Veuillez consulter votre historique des paiements.";
    Icon = XCircle;
    iconCls = "text-[#8B3A3A]";
  }

  return (
    <div className="bg-[#FAFAF7] min-h-screen">
      <Navbar variant="light" />
      <div className="max-w-2xl mx-auto px-6 lg:px-10 py-20">
        <Card className="p-10 lg:p-14 rounded-2xl border-slate-100 text-center" data-testid="payment-return">
          <Icon className={`w-14 h-14 mx-auto mb-6 ${iconCls}`} strokeWidth={1.5} />
          <h1 className="font-serif text-3xl text-[#0B3A2A] mb-4">{title}</h1>
          <p className="text-slate-600 mb-8 leading-relaxed">{body}</p>
          {info && isPaid && (
            <div className="text-sm text-slate-500 mb-8">
              Montant : <span className="font-medium text-[#0B3A2A]">{info.amount_eur?.toFixed(2)} €</span>
            </div>
          )}
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {!isPolling && (
              <Button onClick={() => nav("/dashboard")} data-testid="return-dashboard"
                className="rounded-full bg-[#0B3A2A] hover:bg-[#0B3A2A] text-white h-11 px-6">
                Accéder à mon espace
              </Button>
            )}
            {!isPaid && !isPolling && (
              <Button asChild variant="outline" className="rounded-full h-11 px-6">
                <Link to="/pricing">Retour au référencement</Link>
              </Button>
            )}
          </div>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
